<template>
  <SelectTrigger
    v-bind="forwardedProps"
    :class="cn(selectTriggerVariant(variants), props.class)"
  >
    <slot />
    <SelectIcon as-child>
      <ChevronDown class="w-4 h-4" />
    </SelectIcon>
  </SelectTrigger>
</template>

<script setup>
import { computed, inject } from 'vue';
import { SelectIcon, SelectTrigger, useForwardProps } from 'radix-vue';
import { ChevronDown } from 'lucide-vue-next';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils.js';

const props = defineProps({
  as: {
    type: String,
    required: false,
    default: 'button',
  },
  asChild: {
    type: Boolean,
    required: false,
  },
  disabled: {
    type: Boolean,
    required: false,
  },
});

const delegatedProps = computed(() => {
  const { class: _, ...delegated } = props;

  return delegated;
});

const forwardedProps = useForwardProps(delegatedProps);

const variants = inject('select-variants');

const selectTriggerVariant = cva(
  [
    'text-neutral-300 flex w-full items-center justify-between rounded-md',
    'px-[12px] py-[8px] ring-offset-neutral-900',
    'border data-[state=open]:border-2 data-[state=open]:px-[11px] data-[state=open]:py-[7px]',
    'disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
    'relative before:absolute before:left-0 before:right-0 before:top-0 before:z-10 before:h-full before:w-full hover:before:bg-neutral-300/5',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-primary-700 border-primary-400',
        dark: 'bg-neutral-900 border-neutral-300',
      },
      size: {
        sm: 'h-8 text-xs',
        lg: 'h-10 text-sm',
        xl: 'h-10 text-sm',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'lg',
    },
  }
);
</script>
